import { formatCurrency, formatPercent } from "../lib/api";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

const formatValue = (value, format) => {
  if (value === null || value === undefined) return "—";
  if (format === "currency") return formatCurrency(value);
  if (format === "percent") return formatPercent(value);
  return value;
};

/**
 * Side-by-side 2025 vs 2026 comparison card.
 * rows: [{ label, value2025, value2026, format: 'currency'|'percent'|'number', invertImpact? }]
 */
export const ComparisonCard = ({ rows, totals, testid }) => {
  return (
    <div className="bg-card border-2 border-foreground overflow-hidden" data-testid={testid || "comparison-card"}>
      <div className="grid grid-cols-[1fr_1fr_1fr] border-b-2 border-foreground bg-foreground text-background">
        <div className="p-4 font-mono text-xs uppercase tracking-widest">Metric</div>
        <div className="p-4 font-mono text-xs uppercase tracking-widest border-l border-background/20 text-right">
          2025
        </div>
        <div className="p-4 font-mono text-xs uppercase tracking-widest border-l border-background/20 text-right bg-primary">
          2026
        </div>
      </div>

      {rows.map((row, i) => {
        const num2025 = Number(row.value2025 || 0);
        const num2026 = Number(row.value2026 || 0);
        const diff = num2026 - num2025;
        const isNeutral = diff === 0 || row.hideDelta;
        const positiveImpact = row.invertImpact ? diff < 0 : diff > 0;
        return (
          <div
            key={i}
            className="grid grid-cols-[1fr_1fr_1fr] border-b border-foreground/15 hover:bg-muted/30 transition-colors"
          >
            <div className="p-4 font-body text-sm">
              <div className="font-semibold">{row.label}</div>
              {row.hint && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  {row.hint}
                </div>
              )}
            </div>
            <div className="p-4 text-right font-mono text-sm tabular-nums border-l border-foreground/10">
              {formatValue(row.value2025, row.format)}
            </div>
            <div
              className={`p-4 text-right font-mono text-sm tabular-nums border-l border-foreground/10 bg-primary/5 flex flex-col items-end justify-center`}
            >
              <span className="font-semibold">{formatValue(row.value2026, row.format)}</span>
              {!isNeutral && (
                <span
                  className={`text-[10px] font-mono uppercase tracking-widest mt-1 flex items-center gap-1 ${
                    positiveImpact ? "text-primary" : "text-destructive"
                  }`}
                  data-testid={`delta-${i}`}
                >
                  {diff > 0 ? <ArrowUp className="w-3 h-3" /> : diff < 0 ? <ArrowDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                  {row.format === "currency" ? formatCurrency(Math.abs(diff)) : row.format === "percent" ? formatPercent(Math.abs(diff)) : Math.abs(diff)}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {totals && (
        <div className="grid grid-cols-[1fr_1fr_1fr] bg-foreground text-background">
          <div className="p-5 font-heading text-lg font-bold">{totals.label || "Net Impact"}</div>
          <div className="p-5 text-right font-mono tabular-nums border-l border-background/20">
            {formatValue(totals.value2025, totals.format || "currency")}
          </div>
          <div className="p-5 text-right font-mono tabular-nums border-l border-background/20 bg-primary text-primary-foreground">
            {formatValue(totals.value2026, totals.format || "currency")}
          </div>
        </div>
      )}
    </div>
  );
};
